import { useState, useEffect, useCallback, useRef } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import {
  MessageSquare,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  TrendingUp,
  Eye,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// SMS status config
const smsStatusConfig = {
  queued:      { color: 'bg-gray-100 text-gray-700',   icon: Clock,         label: 'Queued' },
  sent:        { color: 'bg-blue-100 text-blue-700',    icon: Send,          label: 'Sent' },
  delivered:   { color: 'bg-green-100 text-green-700',  icon: CheckCircle,   label: 'Delivered' },
  failed:      { color: 'bg-red-100 text-red-700',      icon: XCircle,       label: 'Failed' },
  undelivered: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle, label: 'Undelivered' },
};

// Email status config
const emailStatusConfig = {
  queued:     { color: 'bg-gray-100 text-gray-700',   icon: Clock,         label: 'Queued' },
  sent:       { color: 'bg-blue-100 text-blue-700',    icon: Send,          label: 'Sent' },
  delivered:  { color: 'bg-green-100 text-green-700',  icon: CheckCircle,   label: 'Delivered' },
  bounced:    { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle, label: 'Bounced' },
  complained: { color: 'bg-red-100 text-red-700',      icon: XCircle,       label: 'Complained' },
  failed:     { color: 'bg-red-100 text-red-700',      icon: XCircle,       label: 'Failed' },
};

function StatusBadge({ status, channel }) {
  const config = channel === 'email' ? emailStatusConfig : smsStatusConfig;
  const cfg = config[status] || config.queued;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function formatPhone(phone) {
  if (!phone) return '';
  if (phone.startsWith('+61') && phone.length === 12) {
    return `+61 ${phone[3]}${phone.slice(4, 6)} ${phone.slice(6, 9)} ${phone.slice(9)}`;
  }
  return phone;
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

export default function SmsHistoryPage() {
  const { getToken } = useTenant();
  const dashData = useDashboardData();
  const preloadedLogs = dashData?.smsLogsPage;
  const preloadedStats = dashData?.smsStats;
  const preloadedCampaigns = dashData?.smsCampaignOptions;

  // Active channel tab
  const [activeChannel, setActiveChannel] = useState('sms');

  // SMS Data
  const [smsLogs, setSmsLogs] = useState(preloadedLogs?.logs || []);
  const [smsStats, setSmsStats] = useState(preloadedStats || null);
  const [smsCampaigns, setSmsCampaigns] = useState(preloadedCampaigns || []);
  const [smsPage, setSmsPage] = useState(1);
  const [smsTotal, setSmsTotal] = useState(preloadedLogs?.total || 0);
  const [smsLoading, setSmsLoading] = useState(!preloadedLogs);
  const [smsStatsLoading, setSmsStatsLoading] = useState(!preloadedStats);

  // Email Data
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [emailPage, setEmailPage] = useState(1);
  const [emailTotal, setEmailTotal] = useState(0);
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailStatsLoading, setEmailStatsLoading] = useState(true);
  const emailInitialFetched = useRef(false);

  // Shared
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // SMS Filters
  const [smsStatusFilter, setSmsStatusFilter] = useState('');
  const [smsCampaignFilter, setSmsCampaignFilter] = useState('');
  const [smsDateFrom, setSmsDateFrom] = useState('');
  const [smsDateTo, setSmsDateTo] = useState('');
  const [smsSearchPhone, setSmsSearchPhone] = useState('');

  // Email Filters
  const [emailStatusFilter, setEmailStatusFilter] = useState('');
  const [emailCampaignFilter, setEmailCampaignFilter] = useState('');
  const [emailDateFrom, setEmailDateFrom] = useState('');
  const [emailDateTo, setEmailDateTo] = useState('');
  const [emailSearchEmail, setEmailSearchEmail] = useState('');

  const SMS_PAGE_SIZE = 25;
  const EMAIL_PAGE_SIZE = 25;
  const smsTotalPages = Math.ceil(smsTotal / SMS_PAGE_SIZE);
  const emailTotalPages = Math.ceil(emailTotal / EMAIL_PAGE_SIZE);

  const skipInitialLogsFetch = useRef(!!preloadedLogs);
  const skipInitialStatsFetch = useRef(!!preloadedStats);
  const skipCampaignsFetch = useRef(!!preloadedCampaigns);

  // ========== SMS FETCHES ==========
  useEffect(() => {
    if (skipCampaignsFetch.current) {
      skipCampaignsFetch.current = false;
      return;
    }
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/sms-logs/campaigns`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSmsCampaigns(await res.json());
      } catch {}
    })();
  }, [getToken]);

  const buildSmsParams = useCallback(() => {
    const params = new URLSearchParams({ page: smsPage.toString(), page_size: SMS_PAGE_SIZE.toString() });
    if (smsStatusFilter) params.set('status', smsStatusFilter);
    if (smsCampaignFilter) params.set('campaign_id', smsCampaignFilter);
    if (smsDateFrom) params.set('date_from', smsDateFrom);
    if (smsDateTo) params.set('date_to', smsDateTo);
    return params;
  }, [smsPage, smsStatusFilter, smsCampaignFilter, smsDateFrom, smsDateTo]);

  const fetchSmsLogs = useCallback(async () => {
    setSmsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = buildSmsParams();
      const res = await fetch(`${BACKEND_URL}/api/sms-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch SMS history');
      const data = await res.json();
      setSmsLogs(data.logs || []);
      setSmsTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSmsLoading(false);
    }
  }, [getToken, buildSmsParams]);

  const fetchSmsStats = useCallback(async () => {
    setSmsStatsLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (smsCampaignFilter) params.set('campaign_id', smsCampaignFilter);
      if (smsDateFrom) params.set('date_from', smsDateFrom);
      if (smsDateTo) params.set('date_to', smsDateTo);
      const res = await fetch(`${BACKEND_URL}/api/sms-logs/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSmsStats(await res.json());
    } catch {} finally {
      setSmsStatsLoading(false);
    }
  }, [getToken, smsCampaignFilter, smsDateFrom, smsDateTo]);

  useEffect(() => {
    if (skipInitialLogsFetch.current) { skipInitialLogsFetch.current = false; return; }
    fetchSmsLogs();
  }, [fetchSmsLogs]);

  useEffect(() => {
    if (skipInitialStatsFetch.current) { skipInitialStatsFetch.current = false; return; }
    fetchSmsStats();
  }, [fetchSmsStats]);

  useEffect(() => { setSmsPage(1); }, [smsStatusFilter, smsCampaignFilter, smsDateFrom, smsDateTo]);

  // ========== EMAIL FETCHES ==========
  const buildEmailParams = useCallback(() => {
    const params = new URLSearchParams({ page: emailPage.toString(), page_size: EMAIL_PAGE_SIZE.toString() });
    if (emailStatusFilter) params.set('status', emailStatusFilter);
    if (emailCampaignFilter) params.set('campaign_id', emailCampaignFilter);
    if (emailDateFrom) params.set('date_from', emailDateFrom);
    if (emailDateTo) params.set('date_to', emailDateTo);
    return params;
  }, [emailPage, emailStatusFilter, emailCampaignFilter, emailDateFrom, emailDateTo]);

  const fetchEmailLogs = useCallback(async () => {
    setEmailLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = buildEmailParams();
      const res = await fetch(`${BACKEND_URL}/api/email-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch email history');
      const data = await res.json();
      setEmailLogs(data.logs || []);
      setEmailTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setEmailLoading(false);
    }
  }, [getToken, buildEmailParams]);

  const fetchEmailStats = useCallback(async () => {
    setEmailStatsLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (emailCampaignFilter) params.set('campaign_id', emailCampaignFilter);
      if (emailDateFrom) params.set('date_from', emailDateFrom);
      if (emailDateTo) params.set('date_to', emailDateTo);
      const res = await fetch(`${BACKEND_URL}/api/email-logs/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setEmailStats(await res.json());
    } catch {} finally {
      setEmailStatsLoading(false);
    }
  }, [getToken, emailCampaignFilter, emailDateFrom, emailDateTo]);

  const fetchEmailCampaigns = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/email-logs/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setEmailCampaigns(await res.json());
    } catch {}
  }, [getToken]);

  // Fetch email data on first tab switch or mount
  useEffect(() => {
    if (activeChannel === 'email' && !emailInitialFetched.current) {
      emailInitialFetched.current = true;
      fetchEmailLogs();
      fetchEmailStats();
      fetchEmailCampaigns();
    }
  }, [activeChannel, fetchEmailLogs, fetchEmailStats, fetchEmailCampaigns]);

  // Re-fetch email when filters/page change (after initial)
  useEffect(() => {
    if (emailInitialFetched.current && activeChannel === 'email') {
      fetchEmailLogs();
    }
  }, [emailPage, emailStatusFilter, emailCampaignFilter, emailDateFrom, emailDateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (emailInitialFetched.current && activeChannel === 'email') {
      fetchEmailStats();
    }
  }, [emailCampaignFilter, emailDateFrom, emailDateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setEmailPage(1); }, [emailStatusFilter, emailCampaignFilter, emailDateFrom, emailDateTo]);

  // Client-side search filters
  const filteredSmsLogs = smsSearchPhone
    ? smsLogs.filter(l => l.to_phone.includes(smsSearchPhone) || (l.customer_name || '').toLowerCase().includes(smsSearchPhone.toLowerCase()))
    : smsLogs;

  const filteredEmailLogs = emailSearchEmail
    ? emailLogs.filter(l => l.to_email.toLowerCase().includes(emailSearchEmail.toLowerCase()) || (l.customer_name || '').toLowerCase().includes(emailSearchEmail.toLowerCase()))
    : emailLogs;

  const smsHasFilters = smsStatusFilter || smsCampaignFilter || smsDateFrom || smsDateTo;
  const emailHasFilters = emailStatusFilter || emailCampaignFilter || emailDateFrom || emailDateTo;

  const clearSmsFilters = () => { setSmsStatusFilter(''); setSmsCampaignFilter(''); setSmsDateFrom(''); setSmsDateTo(''); setSmsSearchPhone(''); };
  const clearEmailFilters = () => { setEmailStatusFilter(''); setEmailCampaignFilter(''); setEmailDateFrom(''); setEmailDateTo(''); setEmailSearchEmail(''); };

  return (
    <div className="p-6 lg:p-8" data-testid="sms-history-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Delivery Logs</h1>
        <p className="text-gray-600 mt-1">Delivery reports for every message sent</p>
      </div>

      {/* Channel Tabs */}
      <Tabs value={activeChannel} onValueChange={setActiveChannel} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="sms" className="flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4" />
            SMS
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-1.5">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
        </TabsList>

        {/* =============== SMS TAB =============== */}
        <TabsContent value="sms">
          {/* SMS Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {smsStatsLoading ? (
              [1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ))
            ) : smsStats ? (
              <>
                <StatCard label="Total Sent" value={smsStats.total_sent.toLocaleString()} sub={`${smsStats.total_segments.toLocaleString()} segments`} icon={Send} color="bg-blue-100 text-blue-600" />
                <StatCard label="Delivered" value={smsStats.total_delivered.toLocaleString()} icon={CheckCircle} color="bg-green-100 text-green-600" />
                <StatCard label="Failed" value={smsStats.total_failed.toLocaleString()} icon={XCircle} color="bg-red-100 text-red-600" />
                <StatCard label="Delivery Rate" value={`${smsStats.delivery_rate}%`} sub={smsStats.total_sent > 0 ? `${smsStats.total_delivered} of ${smsStats.total_sent}` : 'No data'} icon={TrendingUp} color="bg-purple-100 text-purple-600" />
              </>
            ) : null}
          </div>

          {/* SMS Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[140px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select value={smsStatusFilter} onChange={(e) => setSmsStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="">All statuses</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="failed">Failed</option>
                  <option value="undelivered">Undelivered</option>
                  <option value="queued">Queued</option>
                </select>
              </div>
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Campaign</label>
                <select value={smsCampaignFilter} onChange={(e) => setSmsCampaignFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="">All campaigns</option>
                  {smsCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                <input type="date" value={smsDateFrom} onChange={(e) => setSmsDateFrom(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                <input type="date" value={smsDateTo} onChange={(e) => setSmsDateTo(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                  <input type="text" value={smsSearchPhone} onChange={(e) => setSmsSearchPhone(e.target.value)} placeholder="Phone or name..." className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
              </div>
              {smsHasFilters && (
                <button onClick={clearSmsFilters} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {error && activeChannel === 'sms' && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* SMS Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {smsLoading ? (
              <div className="divide-y divide-gray-100">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="p-4 flex items-center gap-4">
                    <Skeleton className="h-5 w-28" /><Skeleton className="h-5 w-36" /><Skeleton className="h-5 w-20 rounded-full" /><Skeleton className="h-5 w-48 flex-1" /><Skeleton className="h-5 w-24" />
                  </div>
                ))}
              </div>
            ) : filteredSmsLogs.length === 0 ? (
              <div className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No SMS found</h3>
                <p className="text-gray-500">{smsHasFilters ? 'Try adjusting your filters.' : 'SMS will appear here once you send a campaign.'}</p>
              </div>
            ) : (
              <>
                <div className="hidden md:grid md:grid-cols-[1fr_1fr_100px_2fr_120px_40px] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span>Recipient</span><span>Campaign</span><span>Status</span><span>Message</span><span>Sent</span><span></span>
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredSmsLogs.map((log) => (
                    <div key={log.id}>
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_100px_2fr_120px_40px] gap-2 md:gap-4 px-4 py-3 hover:bg-gray-50 transition-colors items-center">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{log.customer_name || formatPhone(log.to_phone)}</p>
                          {log.customer_name && <p className="text-xs text-gray-400 truncate">{formatPhone(log.to_phone)}</p>}
                        </div>
                        <p className="text-sm text-gray-600 truncate">{log.campaign_name || <span className="text-gray-400 italic">-</span>}</p>
                        <div><StatusBadge status={log.status} channel="sms" /></div>
                        <p className="text-sm text-gray-500 truncate font-mono">{log.message_body.slice(0, 80)}{log.message_body.length > 80 ? '...' : ''}</p>
                        <p className="text-xs text-gray-400">{formatDate(log.sent_at || log.created_at)}</p>
                        <button onClick={() => setExpandedId(expandedId === log.id ? null : log.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded" title="View details">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                      {expandedId === log.id && (
                        <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-3">
                            <div>
                              <p className="text-xs font-medium text-gray-500">Full Message</p>
                              <p className="text-sm text-gray-800 mt-1 font-mono whitespace-pre-wrap break-words">{log.message_body}</p>
                            </div>
                            <div className="space-y-2">
                              <div><p className="text-xs font-medium text-gray-500">Segments</p><p className="text-sm text-gray-800">{log.segments}</p></div>
                              <div><p className="text-xs font-medium text-gray-500">Provider</p><p className="text-sm text-gray-800">{log.provider || '-'}</p></div>
                              {log.provider_message_id && <div><p className="text-xs font-medium text-gray-500">Provider ID</p><p className="text-sm text-gray-800 font-mono text-xs break-all">{log.provider_message_id}</p></div>}
                            </div>
                            <div className="space-y-2">
                              <div><p className="text-xs font-medium text-gray-500">Created</p><p className="text-sm text-gray-800">{formatDate(log.created_at)}</p></div>
                              {log.sent_at && <div><p className="text-xs font-medium text-gray-500">Sent</p><p className="text-sm text-gray-800">{formatDate(log.sent_at)}</p></div>}
                              {log.delivered_at && <div><p className="text-xs font-medium text-gray-500">Delivered</p><p className="text-sm text-green-700">{formatDate(log.delivered_at)}</p></div>}
                            </div>
                            <div className="space-y-2">
                              {log.failed_at && <div><p className="text-xs font-medium text-gray-500">Failed</p><p className="text-sm text-red-700">{formatDate(log.failed_at)}</p></div>}
                              {log.failure_reason && <div><p className="text-xs font-medium text-gray-500">Failure Reason</p><p className="text-sm text-red-700">{log.failure_reason}</p></div>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {smsTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">{smsTotal.toLocaleString()} SMS total &middot; Page {smsPage} of {smsTotalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setSmsPage(p => Math.max(1, p - 1))} disabled={smsPage === 1} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setSmsPage(p => Math.min(smsTotalPages, p + 1))} disabled={smsPage === smsTotalPages} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* =============== EMAIL TAB =============== */}
        <TabsContent value="email">
          {/* Email Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {emailStatsLoading ? (
              [1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ))
            ) : emailStats ? (
              <>
                <StatCard label="Total Sent" value={(emailStats.total_sent || 0).toLocaleString()} icon={Send} color="bg-purple-100 text-purple-600" />
                <StatCard label="Delivered" value={(emailStats.total_delivered || 0).toLocaleString()} icon={CheckCircle} color="bg-green-100 text-green-600" />
                <StatCard label="Bounced" value={(emailStats.total_bounced || 0).toLocaleString()} icon={AlertTriangle} color="bg-orange-100 text-orange-600" />
                <StatCard label="Bounce Rate" value={`${emailStats.bounce_rate || 0}%`} sub={emailStats.total_complained > 0 ? `${emailStats.total_complained} complaints` : null} icon={TrendingUp} color="bg-red-100 text-red-600" />
              </>
            ) : null}
          </div>

          {/* Email Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[140px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select value={emailStatusFilter} onChange={(e) => setEmailStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="">All statuses</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="bounced">Bounced</option>
                  <option value="complained">Complained</option>
                  <option value="failed">Failed</option>
                  <option value="queued">Queued</option>
                </select>
              </div>
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Campaign</label>
                <select value={emailCampaignFilter} onChange={(e) => setEmailCampaignFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="">All campaigns</option>
                  {emailCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                <input type="date" value={emailDateFrom} onChange={(e) => setEmailDateFrom(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                <input type="date" value={emailDateTo} onChange={(e) => setEmailDateTo(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                  <input type="text" value={emailSearchEmail} onChange={(e) => setEmailSearchEmail(e.target.value)} placeholder="Email or name..." className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
                </div>
              </div>
              {emailHasFilters && (
                <button onClick={clearEmailFilters} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {error && activeChannel === 'email' && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Email Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {emailLoading ? (
              <div className="divide-y divide-gray-100">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="p-4 flex items-center gap-4">
                    <Skeleton className="h-5 w-36" /><Skeleton className="h-5 w-36" /><Skeleton className="h-5 w-20 rounded-full" /><Skeleton className="h-5 w-48 flex-1" /><Skeleton className="h-5 w-24" />
                  </div>
                ))}
              </div>
            ) : filteredEmailLogs.length === 0 ? (
              <div className="p-12 text-center">
                <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No emails found</h3>
                <p className="text-gray-500">{emailHasFilters ? 'Try adjusting your filters.' : 'Emails will appear here once you send an email campaign.'}</p>
              </div>
            ) : (
              <>
                <div className="hidden md:grid md:grid-cols-[1fr_1fr_100px_2fr_120px_40px] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span>Recipient</span><span>Campaign</span><span>Status</span><span>Subject</span><span>Sent</span><span></span>
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredEmailLogs.map((log) => (
                    <div key={log.id}>
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_100px_2fr_120px_40px] gap-2 md:gap-4 px-4 py-3 hover:bg-gray-50 transition-colors items-center">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{log.customer_name || log.to_email}</p>
                          {log.customer_name && <p className="text-xs text-gray-400 truncate">{log.to_email}</p>}
                        </div>
                        <p className="text-sm text-gray-600 truncate">{log.campaign_name || <span className="text-gray-400 italic">-</span>}</p>
                        <div><StatusBadge status={log.status} channel="email" /></div>
                        <p className="text-sm text-gray-500 truncate">{log.subject}</p>
                        <p className="text-xs text-gray-400">{formatDate(log.sent_at || log.created_at)}</p>
                        <button onClick={() => setExpandedId(expandedId === log.id ? null : log.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded" title="View details">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                      {expandedId === log.id && (
                        <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-3">
                            <div>
                              <p className="text-xs font-medium text-gray-500">Subject</p>
                              <p className="text-sm text-gray-800 mt-1">{log.subject}</p>
                            </div>
                            <div className="space-y-2">
                              {log.ses_message_id && <div><p className="text-xs font-medium text-gray-500">SES Message ID</p><p className="text-sm text-gray-800 font-mono text-xs break-all">{log.ses_message_id}</p></div>}
                              {log.opened_at && <div><p className="text-xs font-medium text-gray-500">Opened</p><p className="text-sm text-blue-700">{formatDate(log.opened_at)}</p></div>}
                              {log.clicked_at && <div><p className="text-xs font-medium text-gray-500">Clicked</p><p className="text-sm text-blue-700">{formatDate(log.clicked_at)}</p></div>}
                            </div>
                            <div className="space-y-2">
                              <div><p className="text-xs font-medium text-gray-500">Created</p><p className="text-sm text-gray-800">{formatDate(log.created_at)}</p></div>
                              {log.sent_at && <div><p className="text-xs font-medium text-gray-500">Sent</p><p className="text-sm text-gray-800">{formatDate(log.sent_at)}</p></div>}
                              {log.bounced_at && <div><p className="text-xs font-medium text-gray-500">Bounced</p><p className="text-sm text-orange-700">{formatDate(log.bounced_at)}</p></div>}
                            </div>
                            <div className="space-y-2">
                              {log.complained_at && <div><p className="text-xs font-medium text-gray-500">Complained</p><p className="text-sm text-red-700">{formatDate(log.complained_at)}</p></div>}
                              {log.failed_at && <div><p className="text-xs font-medium text-gray-500">Failed</p><p className="text-sm text-red-700">{formatDate(log.failed_at)}</p></div>}
                              {log.failure_reason && <div><p className="text-xs font-medium text-gray-500">Failure Reason</p><p className="text-sm text-red-700">{log.failure_reason}</p></div>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {emailTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">{emailTotal.toLocaleString()} emails total &middot; Page {emailPage} of {emailTotalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setEmailPage(p => Math.max(1, p - 1))} disabled={emailPage === 1} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setEmailPage(p => Math.min(emailTotalPages, p + 1))} disabled={emailPage === emailTotalPages} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
